import { sendHTTPRequest } from '@/util/http'
import { goalbookUrls, toolkitUrls } from '@/util/links'
import type { Standard, StandardDetail } from '@/standards/standard_types'

export type StandardSummary = Pick<Standard, 'standard_long_ref' | 'standard_short_ref' | 'standard_text'>
export type StandardMatch = StandardSummary & Pick<StandardDetail, 'core_purpose'> & { substandard_text?: string }

const STANDARD_MATCH_FIELDS = ['standard_long_ref', 'standard_short_ref', 'standard_text', 'substandard_text', 'core_purpose']

// Pathways reads these as form fields, and a list of refs this long does not fit in a query string.
async function queryStandardsByRef<T>(setCode: string, standardLongRefs: string[], selectFields: string): Promise<T[]> {
  if (!standardLongRefs.length) return []
  const response = await sendHTTPRequest({
    url: `${goalbookUrls.pathwaysApi}/standards/${setCode}/query`,
    method: 'POST',
    urlEncodedForm: { select_fields: selectFields, standards: standardLongRefs.join(',') }
  })
  return await response.json() as T[]
}

export function getStandardSummaries(setCode: string, standardLongRefs: string[]): Promise<StandardSummary[]> {
  return queryStandardsByRef(setCode, standardLongRefs, 'standard_text,standard_short_ref')
}

// Pathways decides what matches, including text the caller doesn't show.
export async function searchStandards(setCode: string, query: string, signal?: AbortSignal): Promise<StandardMatch[]> {
  const response = await sendHTTPRequest({
    url: `${goalbookUrls.pathwaysApi}/standards/${setCode}/query`,
    method: 'POST',
    urlEncodedForm: { match_str: query, select_fields: STANDARD_MATCH_FIELDS.join(',') },
    signal
  })
  return await response.json() as StandardMatch[]
}

export function getStandardMatches(setCode: string, standardLongRefs: string[]): Promise<StandardMatch[]> {
  return queryStandardsByRef(setCode, standardLongRefs, STANDARD_MATCH_FIELDS.join(','))
}

export async function getValidStandardSets(): Promise<string[]> {
  try {
    const response = await sendHTTPRequest({ url: `${goalbookUrls.pathwaysApi}/standards/sets` })
    return (await response.json()) as string[]
  } catch (e) {
    throw new Error('Failed to get valid standard sets', { cause: e })
  }
}

interface StandardsResponse {
  standards: StandardDetail[]
}

/** Returns the standards in the order of `longRefs`, with null where the server knew no such standard. */
export async function getStandardsForSet(setCode: string, longRefs: string[]): Promise<(StandardDetail | null)[]> {
  try {
    const response = await sendHTTPRequest({
      url: `${toolkitUrls.api}/standards/${setCode.toLowerCase()}/long_refs`,
      method: 'GET',
      urlParams: { list: longRefs.join(','), substandards: 'true' },
      includeCredentials: false
    })
    const { standards } = await response.json() as StandardsResponse
    const standardsByLongRef = new Map(standards.map((standard) => [standard.standard_long_ref, standard]))
    return longRefs.map((longRef) => standardsByLongRef.get(longRef) ?? null)
  } catch (e) {
    throw new Error('Failed to get standards', { cause: e })
  }
}
